import React, { useEffect, useRef, useState, Suspense, lazy as reactLazy } from 'react';
import "@/App.css";
import "@/styles/scroll-engagement.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useScrollReveal from '@/hooks/useScrollReveal';
import { LanguageProvider, useLanguage } from '@/lib/i18n';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AudienceProvider } from '@/lib/audience';
import { useHoverPrefetch } from '@/lib/routePrefetch';

/**
 * lazy() with stale-chunk auto-recovery.
 *
 * After a new deployment, a browser still running the previous build may hold
 * an index.html/main.js that references code-split chunk filenames which no
 * longer exist on the server. The server then returns index.html (text/html)
 * for the missing `*.chunk.js`, producing a ChunkLoadError / MIME error and a
 * hard white-screen crash. We catch that once, force a single reload to pull
 * the fresh build, and only surface the error if reloading didn't help.
 */
const lazy = (factory) => reactLazy(() => factory().catch((err) => {
  const KEY = '__jtr_chunk_reload_at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  // Only auto-reload once per 10s window to avoid reload loops.
  if (Date.now() - last > 10000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
    return new Promise(() => {}); // hold render while the page reloads
  }
  throw err;
}));

const OrganizationManagementPage = lazy(() => import('@/pages/OrganizationManagementPage'));
import { Toaster } from '@/components/ui/sonner';
import { SmartTooltipProvider } from '@/components/ui/SmartTooltip';
import StickySupport from '@/components/layout/StickySupport';
import { useFloatingCollision } from '@/lib/useFloatingCollision';
import SEOHead from '@/components/SEOHead';
import PublicAccessBanner from '@/components/PublicAccessBanner';
import EarlyAccessPopup from '@/components/EarlyAccessPopup';
import EarlyAccessSideButton from '@/components/EarlyAccessSideButton';
import LangPathEnforcer from '@/components/LangPathEnforcer';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { AdminThemeProvider } from '@/lib/adminTheme';
import DashboardErrorBoundary from '@/components/DashboardErrorBoundary';
import HomePage from '@/pages/HomePage';
import NotFoundPage from '@/pages/NotFoundPage';
import DashboardNotFound from '@/components/dashboard/DashboardNotFound';
const PersonalOverviewPage = lazy(() => import('@/pages/PersonalOverviewPage'));
const PersonalFeaturesPage = lazy(() => import('@/pages/PersonalFeaturesPage'));
const PersonalPricingPage = lazy(() => import('@/pages/PersonalPricingPage'));
const PricingCalculatorPreviewPage = lazy(() => import('@/pages/PricingCalculatorPreviewPage'));
const BusinessOverviewPage = lazy(() => import('@/pages/BusinessOverviewPage'));
const BusinessFeaturesPage = lazy(() => import('@/pages/BusinessFeaturesPage'));
const BusinessPricingPage = lazy(() => import('@/pages/BusinessPricingPage'));
const GovernmentOverviewPage = lazy(() => import('@/pages/GovernmentOverviewPage'));
const GovernmentFeaturesPage = lazy(() => import('@/pages/GovernmentFeaturesPage'));
const GovernmentPricingPage = lazy(() => import('@/pages/GovernmentPricingPage'));
const AuthorizedVehiclesDetailPage = lazy(() => import('@/pages/AuthorizedVehiclesDetailPage'));
const BusinessAuthorizedVehiclesDetailPage = lazy(() => import('@/pages/BusinessAuthorizedVehiclesDetailPage'));
const AuthorizedVehicleMonitoringPage = lazy(() => import('@/pages/AuthorizedVehicleMonitoringPage'));
const FamilyVehicleListPage = lazy(() => import('@/pages/FamilyVehicleListPage'));
const FeaturesHubPage = lazy(() => import('@/pages/FeaturesHubPage'));
const FeatureDetailPage = lazy(() => import('@/pages/FeatureDetailPage'));
const PlateTypesPage = lazy(() => import('@/pages/PlateTypesPage'));
const ArchitecturePage = lazy(() => import('@/pages/ArchitecturePage'));
// NOTE: The three Limited-Time Offer pages (personal/business/government)
// were retired in iter 92 — their routes below redirect to /free-trial,
// the single canonical conversion page. LimitedTimeOfferPage.jsx itself was
// deleted in the Professional Polish Audit 2026-02 (Round 3) dead-code
// cleanup after confirming zero reachability across route declarations,
// lazy imports, nav config, feature flags, tests, sitemap/SEO metadata,
// and dynamic import strings. Do NOT add new routes pointing at it.
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const DashboardOverview = lazy(() => import('@/pages/dashboard/DashboardOverview'));
const AdaptiveDashboard = lazy(() => import('@/pages/dashboard/AdaptiveDashboard'));
const OnboardingPage = lazy(() => import('@/pages/dashboard/OnboardingPage'));
const CardsPage = lazy(() => import('@/pages/dashboard/CardsPage'));
const CardMappingPage = lazy(() => import('@/pages/dashboard/CardMappingPage'));
const RecipientsPage = lazy(() => import('@/pages/dashboard/RecipientsPage'));
const JapanETCcardSyncPage = lazy(() => import('@/pages/dashboard/JapanETCcardSyncPage'));
const CardRegistrationStatusPage = lazy(() => import('@/pages/dashboard/CardRegistrationStatusPage'));
const AdminRegistrationAttemptsPage = lazy(() => import('@/pages/admin/AdminRegistrationAttemptsPage'));
const ReportsPage = lazy(() => import('@/pages/dashboard/ReportsPage'));
const ReportsHubPage = lazy(() => import('@/pages/dashboard/ReportsHubPage'));
const DeliverySettingsPage = lazy(() => import('@/pages/dashboard/DeliverySettingsPage'));
const CustomReportsPage = lazy(() => import('@/pages/dashboard/CustomReportsPage'));
const ReimbursementPage = lazy(() => import('@/pages/dashboard/ReimbursementPage'));
const VehicleMonitoringPage = lazy(() => import('@/pages/dashboard/VehicleMonitoringPage'));
const MeisaiHealthPage = lazy(() => import('@/pages/dashboard/MeisaiHealthPage'));
const MeisaiVaultPage = lazy(() => import('@/pages/dashboard/MeisaiVaultPage'));
const EarlyAccessPage = lazy(() => import('@/pages/dashboard/EarlyAccessPage'));
import EarlyAccessLandingPage from '@/pages/EarlyAccessLandingPage';
const EarlyAccessConfirmPage = lazy(() => import('@/pages/EarlyAccessConfirmPage'));
// Eager-imported (NOT lazy): these are P0 paid-traffic conversion landing pages.
// Lazy-loading made the waitlist funnel depend on an async chunk that could
// stall (webpack dev-server on-demand compilation / flaky chunk delivery),
// leaving visitors stuck on the route-loading spinner. Bundling it into the
// main bundle guarantees the Early Access ad pages render immediately. See the
// matching eager EarlyAccessLandingPage import above.
import AdLandingPage from '@/pages/AdLandingPage';
const AdCreativesConsolePage = lazy(() => import('@/pages/AdCreativesConsolePage'));
const EmailPreferencesPage = lazy(() => import('@/pages/EmailPreferencesPage'));
const PlanPage = lazy(() => import('@/pages/dashboard/PlanPage'));
const AIReviewPage = lazy(() => import('@/pages/dashboard/AIReviewPage'));
const ManagerReviewQueuePage = lazy(() => import('@/pages/dashboard/ManagerReviewQueuePage'));
import { EntitlementGate } from '@/components/dashboard/RestrictedAccess';
const SchedulesPage = lazy(() => import('@/pages/dashboard/SchedulesPage'));
const BillingPreviewPage = lazy(() => import('@/pages/dashboard/BillingPreviewPage'));
const SettingsPage = lazy(() => import('@/pages/dashboard/SettingsPage'));
const AccountClosurePage = lazy(() => import('@/pages/dashboard/AccountClosurePage'));
const SupportPage = lazy(() => import('@/pages/dashboard/SupportPage'));
const MeisaiSetupPage = lazy(() => import('@/pages/dashboard/MeisaiSetupPage'));
const SimulatorHarnessPage = lazy(() => import('@/pages/dev/SimulatorHarnessPage'));
const BillingQaHarnessPage = lazy(() => import('@/pages/dev/BillingQaHarnessPage'));
const DesignPreviewPage = lazy(() => import('@/pages/dev/DesignPreviewPage'));
const AdminOverview = lazy(() => import('@/pages/admin/AdminOverview'));
const AdminCommandCenter = lazy(() => import('@/pages/admin/AdminCommandCenter'));
const AdminIssuesPage = lazy(() => import('@/pages/admin/AdminIssuesPage'));
const AdminBillingReadinessPage = lazy(() => import('@/pages/admin/AdminBillingReadinessPage'));
const AdminLaunchChecklist = lazy(() => import('@/pages/admin/AdminLaunchChecklist'));
const AdminReportsPage = lazy(() => import('@/pages/admin/AdminReportsPage'));
const AdminEmailTemplatesPage = lazy(() => import('@/pages/admin/AdminEmailTemplatesPage'));
const AdminEarlyAccessPage = lazy(() => import('@/pages/admin/AdminEarlyAccessPage'));
const AdminEmailCampaignsPage = lazy(() => import('@/pages/admin/AdminEmailCampaignsPage'));
const AdminOgPreviewPage = lazy(() => import('@/pages/admin/AdminOgPreviewPage'));
const AdminBetaOperationsPage = lazy(() => import('@/pages/admin/AdminBetaOperationsPage'));
const AdminAutomationCenterPage = lazy(() => import('@/pages/admin/AdminAutomationCenterPage'));
const AdminMissionControlPage = lazy(() => import('@/pages/admin/AdminMissionControlPage'));
const AdminLaunchWeekOpsPage = lazy(() => import('@/pages/admin/AdminLaunchWeekOpsPage'));
const AdminSecurityEventsPage = lazy(() => import('@/pages/admin/AdminSecurityEventsPage'));
const AdminSecurityCockpitPage = lazy(() => import('@/pages/admin/AdminSecurityCockpitPage'));
const AdminSchedulerHealthPage = lazy(() => import('@/pages/admin/AdminSchedulerHealthPage'));
const AdminProductionDoctorPage = lazy(() => import('@/pages/admin/AdminProductionDoctorPage'));
const AdminProductionAcceptancePage = lazy(() => import('@/pages/admin/AdminProductionAcceptancePage'));
const AdminReportSimulatorPage = lazy(() => import('@/pages/admin/AdminReportSimulatorPage'));
const AdminReportActivityPage = lazy(() => import('@/pages/admin/AdminReportActivityPage'));
const AdminDataHealthPage = lazy(() => import('@/pages/admin/AdminDataHealthPage'));
const AdminQuarantinePage = lazy(() => import('@/pages/admin/AdminQuarantinePage'));
const AdminFederationPage = lazy(() => import('@/pages/admin/AdminFederationPage'));
const AdminFederationDiagnosePage = lazy(() => import('@/pages/admin/AdminFederationDiagnosePage'));
const AdminSyncStatusPage = lazy(() => import('@/pages/admin/AdminSyncStatusPage'));
const AdminMeisaiMonitorPage = lazy(() => import('@/pages/admin/AdminMeisaiMonitorPage'));
const AdminOrgOperationsPage = lazy(() => import('@/pages/admin/AdminOrgOperationsPage'));
const AdminOrgDetailPage = lazy(() => import('@/pages/admin/AdminOrgDetailPage'));
const AdminJobMonitorPage = lazy(() => import('@/pages/admin/AdminJobMonitorPage'));
const AdminSchedulerThroughputPage = lazy(() => import('@/pages/admin/AdminSchedulerThroughputPage'));
const AdminLaunchStatusPage = lazy(() => import('@/pages/admin/AdminLaunchStatusPage'));
const AdminPerformanceScalePage = lazy(() => import('@/pages/admin/AdminPerformanceScalePage'));
const AdminGovernorPage = lazy(() => import('@/pages/admin/AdminGovernorPage'));
const AdminReleaseLogPage = lazy(() => import('@/pages/admin/AdminReleaseLogPage'));
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/AdminAuditLogsPage'));
const AdminExportAuditPage = lazy(() => import('@/pages/admin/AdminExportAuditPage'));
const MarketingCommandCenterPage = lazy(() => import('@/pages/admin/MarketingCommandCenterPage'));
const AdminAccountTypeRequestsPage = lazy(() => import('@/pages/admin/AdminAccountTypeRequestsPage'));
const AdminPhaseBLivePage = lazy(() => import('@/pages/admin/AdminPhaseBLivePage'));
const AdminMeisaiTestAccountsPage = lazy(() => import('@/pages/admin/AdminMeisaiTestAccountsPage'));
const AdminEmailDeliveryHealthPage = lazy(() => import('@/pages/admin/AdminEmailDeliveryHealthPage'));
const AdminSupportMessagesPage = lazy(() => import('@/pages/admin/AdminSupportMessagesPage'));
const AdminSelfRescuePage = lazy(() => import('@/pages/admin/AdminSelfRescuePage'));
const AdminCustomersPage = lazy(() => import('@/pages/admin/AdminCustomersPage'));
const AdminClosedAccountsPage = lazy(() => import('@/pages/admin/AdminClosedAccountsPage'));
const AdminFraudWatchPage = lazy(() => import('@/pages/admin/AdminFraudWatchPage'));
const AdminCustomerDetailPage = lazy(() => import('@/pages/admin/AdminCustomerDetailPage'));
const AdminHomepageMediaPage = lazy(() => import('@/pages/admin/AdminHomepageMediaPage'));
const AdminCustomer360Page = lazy(() => import('@/pages/admin/AdminCustomer360Page'));
const AdminDiagnosticPage = lazy(() => import('@/pages/admin/AdminDiagnosticPage'));
const AdminHostToolsPage = lazy(() => import('@/pages/admin/AdminHostToolsPage'));
const AdminExportsPage = lazy(() => import('@/pages/admin/AdminExportsPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminWorkflowPlaybookPage = lazy(() => import('@/pages/admin/AdminWorkflowPlaybookPage'));
const ReferralsPage = lazy(() => import('@/pages/dashboard/ReferralsPage'));
// Legacy report pages — removed in iter66c (URLs redirect via LegacyRedirect above)
const PricingChooserPage = lazy(() => import('@/pages/PricingChooserPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const HowItWorksPage = lazy(() => import('@/pages/HowItWorksPage'));
const SecurityPage = lazy(() => import('@/pages/SecurityPage'));
const PhishingAwarenessPage = lazy(() => import('@/pages/PhishingAwarenessPage'));
const LegalETCSetupPage = lazy(() => import('@/pages/LegalETCSetupPage'));
const BusinessPage = lazy(() => import('@/pages/BusinessPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
// import FreeReportsPage from '@/pages/FreeReportsPage';     // legacy redirect
// import DailyReportsPage from '@/pages/DailyReportsPage';   // legacy redirect
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const OfficialResourcesPage = lazy(() => import('@/pages/OfficialResourcesPage'));
// Government & Military toll cluster — RESTORED 2026-06 (original pre-refactor
// pages reinstated from snapshot, forward, with no codebase rollback).
const GovernmentMilitaryTollPage = lazy(() => import('@/pages/GovernmentMilitaryTollPage'));
const GovTollHubPage = lazy(() => import('@/pages/govtoll/GovTollHubPage'));
const GovOfficialVehicleTollsPage = lazy(() => import('@/pages/govtoll/GovOfficialVehicleTollsPage'));
const PoliceFireEmergencyTollPage = lazy(() => import('@/pages/govtoll/PoliceFireEmergencyTollPage'));
const OfficialConvoyTollPage = lazy(() => import('@/pages/govtoll/OfficialConvoyTollPage'));
const DietMembersTollPage = lazy(() => import('@/pages/govtoll/DietMembersTollPage'));
const WhoPaysGovVehicleTollsPage = lazy(() => import('@/pages/govtoll/WhoPaysGovVehicleTollsPage'));
const GovFleetRecordkeepingPage = lazy(() => import('@/pages/govtoll/GovFleetRecordkeepingPage'));
const MofaDiplomaticVehicleTollsPage = lazy(() => import('@/pages/govtoll/MofaDiplomaticVehicleTollsPage'));
const GovVehicleResourcesPage = lazy(() => import('@/pages/govtoll/GovVehicleResourcesPage'));
const PublicSafetyFleetRecordsPage = lazy(() => import('@/pages/govtoll/PublicSafetyFleetRecordsPage'));
const WhyJapanTollReceiptsPage = lazy(() => import('@/pages/WhyJapanTollReceiptsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'));
const DataHandlingPage = lazy(() => import('@/pages/DataHandlingPage'));
const ConsentPolicyPage = lazy(() => import('@/pages/ConsentPolicyPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage'));
const RoadmapPage = lazy(() => import('@/pages/RoadmapPage'));
const LiveTestingPage = lazy(() => import('@/pages/LiveTestingPage'));
const CommercialTransactionsPage = lazy(() => import('@/pages/CommercialTransactionsPage'));
const RefundPolicyPage = lazy(() => import('@/pages/RefundPolicyPage'));
const MarketOpportunityPage = lazy(() => import('@/pages/MarketOpportunityPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const FederationMergeConfirmPage = lazy(() => import('@/pages/FederationMergeConfirmPage'));
const SsoRedeemPage = lazy(() => import('@/pages/SsoRedeemPage'));
const AccountRecoveryPage = lazy(() => import('@/pages/AccountRecoveryPage'));
const RecoveryMeisaiPage = lazy(() => import('@/pages/RecoveryMeisaiPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const OurStoryPage = lazy(() => import('@/pages/OurStoryPage')); // exports the "Founder's Message" page (file kept for git history)
const CorporateProfilePage = lazy(() => import('@/pages/CorporateProfilePage'));
const TestimonialsPage = lazy(() => import('@/pages/TestimonialsPage'));
const SitemapPage = lazy(() => import('@/pages/SitemapPage'));
// import PremiumReportsPage from '@/pages/PremiumReportsPage'; // legacy redirect
// (legacy BusinessReportsPage removed — superseded by the new pricing/overview templates)
const JapaneseDriversPage = lazy(() => import('@/pages/JapaneseDriversPage'));
const ForeignResidentsPage = lazy(() => import('@/pages/ForeignResidentsPage'));
const FreeTrialPage = lazy(() => import('@/pages/FreeTrialPage'));
const WhyAutomatedNotManualPage = lazy(() => import('@/pages/WhyAutomatedNotManualPage'));
const SampleOutputsPage = lazy(() => import('@/pages/SampleOutputsPage'));
const ETCReceiptGuidePage = lazy(() => import('@/pages/ETCReceiptGuidePage'));
const ETCExpenseGuidePage = lazy(() => import('@/pages/ETCExpenseGuidePage'));
const ETCPdfCsvGuidePage = lazy(() => import('@/pages/ETCPdfCsvGuidePage'));
// Iter 264 (2026-05-24): freee / Money Forward CSV import guide (neutral, no partnership)
const ETCCsvFreeeMfGuidePage = lazy(() => import('@/pages/ETCCsvFreeeMfGuidePage'));
// Iter 267 (2026-05-24): Carshare / rental-car ETC receipts guide (neutral, no partnership)
const ETCCarshareGuidePage = lazy(() => import('@/pages/ETCCarshareGuidePage'));
// Iter 268 (2026-05-24): Rental-car operator ETC receipt guide (neutral, no partnership)
const RentalCarOperatorETCGuidePage = lazy(() => import('@/pages/RentalCarOperatorETCGuidePage'));
// Iter 269 (2026-05-24): ETC Mileage Points × statement management guide (neutral, no partnership)
const ETCMileagePointsStatementGuidePage = lazy(() => import('@/pages/ETCMileagePointsStatementGuidePage'));
// Iter 273 (2026-05-24): 4 high-volume Keyword-Planner-aligned guides (all neutral)
const HighwayTollFeeVsEtcUsageRecordsGuidePage = lazy(() => import('@/pages/HighwayTollFeeVsEtcUsageRecordsGuidePage'));
const ETCCardUsageRecordsReceiptsGuidePage = lazy(() => import('@/pages/ETCCardUsageRecordsReceiptsGuidePage'));
const ETCObuManagementNumberSetupGuidePage = lazy(() => import('@/pages/ETCObuManagementNumberSetupGuidePage'));
const HighwayTollAccountingTaxExpenseGuidePage = lazy(() => import('@/pages/HighwayTollAccountingTaxExpenseGuidePage'));
// Sprint 2 (2026-05-23): primary Japanese SEO pillar page
const ETCRiyouMeisaiPillarPage = lazy(() => import('@/pages/ETCRiyouMeisaiPillarPage'));
// Sprint 3 (2026-05-23): "反映されない・遅い" pillar
const ETCMeisaiHanEiOsoiPage = lazy(() => import('@/pages/ETCMeisaiHanEiOsoiPage'));
// Sprint 4 (2026-05-23): ETC利用照会サービス vs JTR comparison pillar
const ETCMeisaiVsJTRComparisonPage = lazy(() => import('@/pages/ETCMeisaiVsJTRComparisonPage'));
// Sprint 5 (2026-05-23): 法人ETC明細管理 pillar
const HoujinETCMeisaiKanriPage = lazy(() => import('@/pages/HoujinETCMeisaiKanriPage'));
// Iter 281 (2026-05-24): ETC利用証明書とETC領収書の違い dedicated guide
const ETCRiyouShoumeishoRyoshushoChigaiGuidePage = lazy(() => import('@/pages/ETCRiyouShoumeishoRyoshushoChigaiGuidePage'));
// Iter 282 (2026-05-24): ETC再セットアップ / セットアップ証明書 workflow guide
const ETCResetupSetupCertificateGuidePage = lazy(() => import('@/pages/ETCResetupSetupCertificateGuidePage'));
// Iter 283 (2026-05-24): ETCマイレージ login / registration troubleshooting guide
const ETCMileageLoginTroubleshootingGuidePage = lazy(() => import('@/pages/ETCMileageLoginTroubleshootingGuidePage'));
// Iter 284 (2026-05-24): ETC利用照会サービス registration step-by-step guide
const ETCRiyouShoukaiRegistrationGuidePage = lazy(() => import('@/pages/ETCRiyouShoukaiRegistrationGuidePage'));
// Wave 1 / Iter 285-288 (2026-05-24): accounting & retention cluster
const ETCMeisaiHozonKikanGuidePage = lazy(() => import('@/pages/ETCMeisaiHozonKikanGuidePage'));
const KoutsuhiShouhizeiEtcMeisaiGuidePage = lazy(() => import('@/pages/KoutsuhiShouhizeiEtcMeisaiGuidePage'));
const KoutsuhiShiwakeEtcMeisaiGuidePage = lazy(() => import('@/pages/KoutsuhiShiwakeEtcMeisaiGuidePage'));
const ETCMeisaiDenshiChoboHozonHouGuidePage = lazy(() => import('@/pages/ETCMeisaiDenshiChoboHozonHouGuidePage'));
// Wave 2 / Iter 289 (2026-05-24): display-not-showing + historical-records combined troubleshooting
const ETCMeisaiHyojiSarenaiKakoGuidePage = lazy(() => import('@/pages/ETCMeisaiHyojiSarenaiKakoGuidePage'));
// Wave 6 / Iter 290 (2026-05-24): 経費精算スターターキット cluster-hub
const ETCKeihiSeisanStarterKitGuidePage = lazy(() => import('@/pages/ETCKeihiSeisanStarterKitGuidePage'));
const ETCPhishingSafetyPage = lazy(() => import('@/pages/ETCPhishingSafetyPage'));
const ETCPrinterGuidePage = lazy(() => import('@/pages/ETCPrinterGuidePage'));
const GuidesHubPage = lazy(() => import('@/pages/GuidesHubPage'));
const AllGuidesListPage = lazy(() => import('@/pages/AllGuidesListPage'));
const TrustPage = lazy(() => import('@/pages/TrustPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const ETCCardNumberFormatsPage = lazy(() => import('@/pages/ETCCardNumberFormatsPage'));
const EmailChangeRevertPage = lazy(() => import('@/pages/EmailChangeRevertPage'));
const PoliciesPage = lazy(() => import('@/pages/PoliciesPage'));
const BusinessSuiteLandingPage = lazy(() => import('@/pages/BusinessSuiteLandingPage'));
const FreeWeeklyPlanPage = lazy(() => import('@/pages/FreeWeeklyPlanPage'));
const TollTripStatusCheckerPage = lazy(() => import('@/pages/TollTripStatusCheckerPage'));
import { SEO_LANDING_PAGE_SLUGS } from '@/data/seoLandingPagesSlugs';
// Iter 241 — Lazy wrapper for guides so the ~2.4MB
// `seoLandingPages.js` content payload is excluded from the initial
// bundle. The first navigation to /guides/<slug> triggers the chunk
// download.
const LazySeoLandingPage = lazy(() => import('@/pages/SeoLandingPage'));
const SuspendedSeoLandingPage = (props) => (
  <Suspense fallback={
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin h-8 w-8 border-2 border-blue-500 rounded-full border-t-transparent" />
    </div>
  }>
    <LazySeoLandingPage {...props} />
  </Suspense>
);
const BusinessSuiteApplyPage = lazy(() => import('@/pages/dashboard/BusinessSuiteApplyPage'));
const BusinessSuiteDashboardPage = lazy(() => import('@/pages/dashboard/BusinessSuiteDashboardPage'));
const BusinessSuiteMeisaiPage = lazy(() => import('@/pages/dashboard/BusinessSuiteMeisaiPage'));
const BusinessSuiteCardsPage = lazy(() => import('@/pages/dashboard/BusinessSuiteCardsPage'));
const MeisaiAccountWizard = lazy(() => import('@/pages/dashboard/MeisaiAccountWizard'));
const CardJobFlowPage = lazy(() => import('@/pages/dashboard/CardJobFlowPage'));
const CardManagementHubPage = lazy(() => import('@/pages/dashboard/CardManagementHubPage'));
const BulkImportWizardPage = lazy(() => import('@/pages/dashboard/BulkImportWizardPage'));
const CardBillingPreviewPage = lazy(() => import('@/pages/dashboard/CardBillingPreviewPage'));
const VehicleManagementPage = lazy(() => import('@/pages/dashboard/VehicleManagementPage'));
const RedFlagDashboardPage = lazy(() => import('@/pages/dashboard/RedFlagDashboardPage'));
const ScheduledReportsPage = lazy(() => import('@/pages/dashboard/ScheduledReportsPage'));
const OperationsDashboardPage = lazy(() => import('@/pages/dashboard/OperationsDashboardPage'));
const GroupManagerWorkspacePage = lazy(() => import('@/pages/dashboard/GroupManagerWorkspacePage'));
const QueueCenterPage = lazy(() => import('@/pages/dashboard/QueueCenterPage'));
const NotificationCenterPage = lazy(() => import('@/pages/dashboard/NotificationCenterPage'));
const GroupHierarchyPage = lazy(() => import('@/pages/dashboard/GroupHierarchyPage'));
const FleetActivationPage = lazy(() => import('@/pages/dashboard/FleetActivationPage'));
const ActivationBlockerQueuePage = lazy(() => import('@/pages/dashboard/ActivationBlockerQueuePage'));
const CardReadinessPage = lazy(() => import('@/pages/dashboard/CardReadinessPage'));
const ReportTransparencyPage = lazy(() => import('@/pages/dashboard/ReportTransparencyPage'));
const NotificationVisibilityPage = lazy(() => import('@/pages/dashboard/NotificationVisibilityPage'));
const TrackOverviewPage = lazy(() => import('@/pages/dashboard/TrackOverviewPage'));
const VehicleReadinessPage = lazy(() => import('@/pages/dashboard/VehicleReadinessPage'));
const AccountManagerPage = lazy(() => import('@/pages/dashboard/AccountManagerPage'));
const ReportOnlyDashboardPage = lazy(() => import('@/pages/dashboard/ReportOnlyDashboardPage'));
const VehicleBulkImportPage = lazy(() => import('@/pages/dashboard/VehicleBulkImportPage'));
const CardDetailPage = lazy(() => import('@/pages/dashboard/CardDetailPage'));
const VehicleDetailPage = lazy(() => import('@/pages/dashboard/VehicleDetailPage'));
const AdminAbuseReviewPage = lazy(() => import('@/pages/admin/AdminAbuseReviewPage'));
const AdminCardBillingPage = lazy(() => import('@/pages/admin/AdminCardBillingPage'));
const BusinessSuiteGroupsPage = lazy(() => import('@/pages/dashboard/BusinessSuiteGroupsPage'));
const OrgGroupManagementPage = lazy(() => import('@/pages/dashboard/OrgGroupManagementPage'));
const OrgInviteAcceptPage = lazy(() => import('@/pages/dashboard/OrgInviteAcceptPage'));
const BusinessSuiteBulkReportsPage = lazy(() => import('@/pages/dashboard/BusinessSuiteBulkReportsPage'));
const BusinessSuiteAlertsPage = lazy(() => import('@/pages/dashboard/BusinessSuiteAlertsPage'));
const SpendAlertsPage = lazy(() => import('@/pages/dashboard/SpendAlertsPage'));
const FamilyAuthorizedVehiclesPage = lazy(() => import('@/pages/dashboard/FamilyAuthorizedVehiclesPage'));
const SelfRescuePage = lazy(() => import('@/pages/dashboard/SelfRescuePage'));
const AdminBusinessApplicationsPage = lazy(() => import('@/pages/admin/AdminBusinessApplicationsPage'));
const AdminReportFailuresPage = lazy(() => import('@/pages/admin/AdminReportFailuresPage'));
const AdminFailureRecoveryPage = lazy(() => import('@/pages/admin/AdminFailureRecoveryPage'));
const AdminErrorIntelligencePage = lazy(() => import('@/pages/admin/AdminErrorIntelligencePage'));
const AdminVisitorsPage = lazy(() => import('@/pages/admin/AdminVisitorsPage'));
const AdminVisitorDetailPage = lazy(() => import('@/pages/admin/AdminVisitorDetailPage'));
const AdminTestimonialTranslationsPage = lazy(() => import('@/pages/admin/AdminTestimonialTranslationsPage'));
import VisitorBeacon from '@/components/VisitorBeacon';
import PublicSystemStatusWidget from '@/components/PublicSystemStatusWidget';
import { MetaPixelRouter } from '@/lib/metaPixel';
import { captureVisitor } from '@/lib/attribution';

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin h-8 w-8 border-2 border-blue-500 rounded-full border-t-transparent" />
    </div>
  );
  if (!user) {
    // Preserve the page the user was trying to reach (e.g. a deep link
    // from an email to /dashboard/...) so LoginPage can send them back
    // there after a successful sign-in instead of the generic dashboard.
    // We persist to sessionStorage (durable across the /login → /ja/login
    // locale redirect, which drops React Router state) AND pass router
    // state for the happy path.
    const dest = `${location.pathname}${location.search}${location.hash}`;
    try { window.sessionStorage.setItem('jtr_post_login_redirect', dest); } catch { /* noop */ }
    return <Navigate to="/login" replace state={{ from: dest }} />;
  }
  if (adminOnly && user.role !== 'admin' && user.role !== 'host') return <Navigate to="/dashboard" replace />;
  return children;
}

// Legacy-page redirector — used by publicPages entries below so the
// language-prefixed URLs (e.g. /en/pricing, /ja/free-weekly-reports)
// transparently land on the current IA pages. Avoids dead clicks from
// older inbound links, the footer, sitemap and guide cross-links.
//
// SEO hygiene: while the redirect is mounted we inject
// <meta name="robots" content="noindex,follow"> so Google deindexes the
// deprecated URL and consolidates link-equity onto the canonical
// destination. Without this, an SPA client-side redirect can leave the
// deprecated URL indexed alongside the destination and split page rank.
const LegacyRedirect = ({ to }) => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,follow';
    meta.setAttribute('data-legacy-redirect', '1');
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch (_) { /* already gone */ }
    };
  }, []);
  return <Navigate to={to} replace />;
};

// Syncs site language from logged-in user's saved preference
function LanguageSync() {
  const { user } = useAuth();
  const { syncFromUser } = useLanguage();
  useEffect(() => {
    if (user && (user.language || user.preferred_language)) {
      syncFromUser(user.language || user.preferred_language);
    }
  }, [user, syncFromUser]);
  return null;
}

// StickySupport (chat + email support widgets) is hidden on /admin routes
// because admin/host users don't need customer-facing support shortcuts.
function ConditionalStickySupport() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  // Keep the dedicated ad/campaign landing pages distraction-free (single CTA).
  if (location.pathname.includes('/early-access-')) return null;
  // The private /design-preview reference build must render its own
  // Swiss-styled Support widget matrix only — no production widgets.
  if (location.pathname.startsWith('/design-preview')) return null;
  return <StickySupport />;
}

// PublicAccessBanner (env-status banner) is suppressed on the private
// /design-preview reference build — that page must show ONLY the Swiss
// design-system matrix, never legacy production chrome.
function ConditionalPublicAccessBanner() {
  const location = useLocation();
  if (location.pathname.startsWith('/design-preview')) return null;
  return <PublicAccessBanner />;
}

// Iter 242 — Public system-status pill (customer-facing). Hidden
// inside the /admin surface (admins use the full Repair Center) and
// inside the login/register flows to keep them distraction-free.
function ConditionalPublicStatus() {
  const location = useLocation();
  const { lang } = useLanguage();
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname.startsWith('/login')) return null;
  if (location.pathname.startsWith('/register')) return null;
  // Iter 334s — Hide the public Status pill inside the authenticated
  // customer dashboard. The orange "Status" chip the founder flagged
  // confused customers who couldn't tell whether it referred to their
  // account, their ETC Usage Inquiry Service (MEISAI) connection, or JTR's platform status. The
  // dashboard already exposes the relevant signals (ETC Usage Inquiry Service (MEISAI) Connection
  // page, Needs Attention banner, etc.), so the marketing-flavour
  // widget belongs on public/marketing surfaces only.
  if (location.pathname.startsWith('/dashboard')) return null;
  // Distraction-free ad/campaign landing pages.
  if (location.pathname.includes('/early-access-')) return null;
  // The private /design-preview reference build must show ONLY its own
  // Swiss-styled matrix — no legacy production status widget.
  if (location.pathname.startsWith('/design-preview')) return null;
  return <PublicSystemStatusWidget ja={lang === 'ja'} />;
}

function PP({ title, titleJa }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main id="main-content" className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-3xl sm:text-4xl font-semibold text-[#0A192F]">{title}</h1>
        <p className="mt-4 text-slate-500">This page is being built. / このページは構築中です。</p>
      </main>
      <Footer />
    </div>
  );
}

function DP({ title }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold text-[#0A192F]">{title}</h2>
      <p className="mt-2 text-slate-500">Coming soon. / 近日公開。</p>
    </div>
  );
}

const publicPages = [
  // ── Legacy URLs — redirect to current IA ────────────────────
  // The components themselves still exist for now; we route the URLs
  // through `LegacyRedirect` so every inbound link lands on the
  // current Personal/Business pages instead of stale layouts.
  { path: 'pricing',              title: 'Pricing → Track Chooser',      component: PricingChooserPage },
  { path: 'free-weekly-reports',  title: 'Free Reports → Free Weekly',     component: () => <LegacyRedirect to="/free-weekly" /> },
  { path: 'daily-reports',        title: 'Daily Reports → Personal Features', component: () => <LegacyRedirect to="/personal/features" /> },
  { path: 'premium-reports',      title: 'Premium → Personal Pricing',    component: () => <LegacyRedirect to="/personal/pricing" /> },
  { path: 'business-reports',     title: 'Business Reports → Business Overview', component: () => <LegacyRedirect to="/business" /> },
  { path: 'how-it-works', title: 'How It Works', component: HowItWorksPage },
  { path: 'free-trial', title: 'Free Trial', component: FreeTrialPage },
  { path: 'why-automated-not-manual', title: 'Why Automated', component: WhyAutomatedNotManualPage },
  { path: 'sample-outputs', title: 'Sample Outputs', component: SampleOutputsPage },
  { path: 'japanese-drivers', title: 'Japanese Drivers', component: JapaneseDriversPage },
  { path: 'foreign-residents', title: 'Foreign Residents', component: ForeignResidentsPage },
  // 'business' route now served by BusinessOverviewPage (iter64 IA restructure).
  // The legacy BusinessPage is still accessible at /business-legacy for redirects.
  { path: 'business-legacy', title: 'Business (legacy)', component: BusinessPage },
  { path: 'legal-etc-setup', title: 'Legal ETC Setup', component: LegalETCSetupPage },
  { path: 'security', title: 'Security', component: SecurityPage },
  { path: 'security/phishing-awareness', title: 'Phishing Awareness', component: PhishingAwarenessPage },
  { path: 'faq', title: 'FAQ', component: FAQPage },
  { path: 'contact', title: 'Contact', component: ContactPage },
  // Public Support + Unsubscribe landings — referenced by email footers
  // (e.g. japantollreceipts.jp/support, /unsubscribe and their /en, /ja
  // variants). Support reuses the public Contact page; Unsubscribe reuses
  // the Email Preferences page (handles the ?token= one-click flow).
  { path: 'support', title: 'Support', component: ContactPage },
  { path: 'unsubscribe', title: 'Unsubscribe', component: EmailPreferencesPage },
  { path: 'etc-ryoshusho-riyoumeisai-guide', title: 'ETC Receipt Guide', component: ETCReceiptGuidePage },
  { path: 'japan-etc-receipt-guide', title: 'ETC Receipt Guide', component: ETCReceiptGuidePage },
  { path: 'etc-keihi-seisan-report', title: 'Business Expense Guide', component: ETCExpenseGuidePage },
  { path: 'japan-toll-expense-reports', title: 'Toll Expense Reports', component: ETCExpenseGuidePage },
  { path: 'etc-pdf-csv-download-guide', title: 'PDF/CSV Guide', component: ETCPdfCsvGuidePage },
  { path: 'etc-pdf-csv-report-guide', title: 'PDF/CSV Report Guide', component: ETCPdfCsvGuidePage },
  // Iter 264 (2026-05-24): freee / Money Forward Cloud import guide.
  // Strictly neutral wording — no partnership / certification / integration claims.
  { path: 'guides/etc-csv-freee-moneyforward-import-guide', title: 'ETC CSV → freee / Money Forward Guide', component: ETCCsvFreeeMfGuidePage },
  // Iter 267 (2026-05-24): Carshare / rental-car ETC receipts guide.
  // Neutral wording — no operator partnership / certification claims.
  { path: 'guides/etc-carshare-ryoshusho-guide', title: 'Carshare ETC Receipt Guide', component: ETCCarshareGuidePage },
  { path: 'guides/etc-carshare-receipt-guide', title: 'Carshare ETC Receipt Guide (EN)', component: ETCCarshareGuidePage },
  // Iter 268 (2026-05-24): Rental-car operator ETC receipt guide.
  // Strictly neutral wording — no operator partnership / certification claims.
  { path: 'guides/rental-car-company-etc-receipt-guide', title: 'Rental-Car ETC Receipt Guide', component: RentalCarOperatorETCGuidePage },
  // Iter 269 (2026-05-24): ETC Mileage Points × statement management guide.
  // Strictly neutral wording — no NEXCO / Shutoko / Hanshin / mileage-office
  // partnership claim; JTR does not calculate, redeem, or guarantee points.
  { path: 'guides/etc-mileage-points-meisai-management-guide', title: 'ETCマイレージポイント × 利用明細 管理ガイド', component: ETCMileagePointsStatementGuidePage },
  { path: 'guides/etc-mileage-points-statement-management-guide', title: 'ETC Mileage Points Statement Management Guide', component: ETCMileagePointsStatementGuidePage },
  // Iter 273 (2026-05-24): 4 Keyword-Planner-aligned neutral guides.
  // No fee-calculator / card-recommendation / setup-vendor /
  // tax-certification claims. Each page funnels into JTR core pages.
  { path: 'guides/highway-toll-fee-vs-etc-usage-records-guide', title: '高速道路料金 × ETC利用明細 整理ガイド', component: HighwayTollFeeVsEtcUsageRecordsGuidePage },
  { path: 'guides/etc-card-usage-records-receipts-guide', title: 'ETCカード利用明細・領収書ガイド', component: ETCCardUsageRecordsReceiptsGuidePage },
  { path: 'guides/etc-obu-management-number-setup-guide', title: 'ETC車載器管理番号・セットアップ証明書ガイド', component: ETCObuManagementNumberSetupGuidePage },
  { path: 'guides/highway-toll-accounting-tax-expense-guide', title: '高速代勘定科目・消費税・経費精算ガイド', component: HighwayTollAccountingTaxExpenseGuidePage },
  // Sprint 2 (2026-05-23): Japanese ETC利用明細 pillar guide.
  // EN alias kept symmetric but the JA path is the canonical SEO target.
  { path: 'guides/etc-riyou-meisai-kanzen-guide', title: 'ETC利用明細 完全ガイド', component: ETCRiyouMeisaiPillarPage },
  { path: 'guides/etc-usage-statement-complete-guide', title: 'ETC Usage Statement Pillar Guide', component: ETCRiyouMeisaiPillarPage },
  // Sprint 3 (2026-05-23): "ETC明細が反映されない・遅い" pillar.
  { path: 'guides/etc-meisai-han-ei-osoi-riyu', title: 'ETC明細 反映遅延ガイド', component: ETCMeisaiHanEiOsoiPage },
  { path: 'guides/etc-statement-delay-guide', title: 'ETC Statement Delay Guide', component: ETCMeisaiHanEiOsoiPage },
  // Sprint 4 (2026-05-23): ETC利用照会サービス vs JTR comparison.
  { path: 'comparison/etc-riyou-shoukai-vs-jtr', title: 'ETC利用照会サービスとの違い', component: ETCMeisaiVsJTRComparisonPage },
  { path: 'comparison/etc-inquiry-service-vs-jtr', title: 'vs ETC Inquiry Service', component: ETCMeisaiVsJTRComparisonPage },
  // Sprint 5 (2026-05-23): 法人ETC明細管理 commercial pillar.
  { path: 'guides/houjin-etc-meisai-kanri', title: '法人ETC明細管理ガイド', component: HoujinETCMeisaiKanriPage },
  { path: 'guides/corporate-etc-statement-management', title: 'Corporate ETC Statement Management Guide', component: HoujinETCMeisaiKanriPage },
  // Iter 281 (2026-05-24): ETC利用証明書とETC領収書の違い dedicated guide.
  { path: 'guides/etc-riyou-shoumeisho-ryoshusho-chigai-guide', title: 'ETC利用証明書とETC領収書の違い', component: ETCRiyouShoumeishoRyoshushoChigaiGuidePage },
  { path: 'guides/etc-usage-certificate-vs-receipt-difference-guide', title: 'ETC Usage Certificate vs ETC Receipt', component: ETCRiyouShoumeishoRyoshushoChigaiGuidePage },
  // Iter 282 (2026-05-24): ETC再セットアップ / セットアップ証明書 workflow guide.
  { path: 'guides/etc-resetup-setup-certificate-guide', title: 'ETC再セットアップ・セットアップ証明書ガイド', component: ETCResetupSetupCertificateGuidePage },
  { path: 'guides/etc-resetup-setup-certificate-en-guide', title: 'ETC Re-Setup & Setup Certificate Guide', component: ETCResetupSetupCertificateGuidePage },
  // Iter 283 (2026-05-24): ETCマイレージ login / registration troubleshooting guide.
  { path: 'guides/etc-mileage-login-registration-troubleshooting-guide', title: 'ETCマイレージ ログイン・登録トラブル解決ガイド', component: ETCMileageLoginTroubleshootingGuidePage },
  { path: 'guides/etc-mileage-login-registration-troubleshooting-en-guide', title: 'ETC Mileage Login & Registration Troubleshooting', component: ETCMileageLoginTroubleshootingGuidePage },
  // Iter 284 (2026-05-24): ETC利用照会サービス registration step-by-step guide.
  { path: 'guides/etc-riyou-shoukai-service-registration-guide', title: 'ETC利用照会サービス 登録方法ガイド', component: ETCRiyouShoukaiRegistrationGuidePage },
  { path: 'guides/etc-usage-inquiry-service-registration-guide', title: 'ETC Usage Inquiry Service Registration Guide', component: ETCRiyouShoukaiRegistrationGuidePage },
  // Wave 1 / Iter 285-288 (2026-05-24): accounting & retention cluster (4 pages).
  { path: 'guides/etc-meisai-hozon-kikan-guide', title: 'ETC利用明細の保存期間ガイド', component: ETCMeisaiHozonKikanGuidePage },
  { path: 'guides/etc-statement-retention-period-guide', title: 'ETC Statement Retention Period Guide', component: ETCMeisaiHozonKikanGuidePage },
  { path: 'guides/koutsuhi-shouhizei-etc-meisai-guide', title: '高速代の消費税ガイド', component: KoutsuhiShouhizeiEtcMeisaiGuidePage },
  { path: 'guides/highway-toll-consumption-tax-etc-statement-guide', title: 'Highway Toll Consumption Tax Guide', component: KoutsuhiShouhizeiEtcMeisaiGuidePage },
  { path: 'guides/koutsuhi-shiwake-etc-meisai-guide', title: '高速代の仕訳ガイド', component: KoutsuhiShiwakeEtcMeisaiGuidePage },
  { path: 'guides/highway-toll-journal-entry-etc-statement-guide', title: 'Highway Toll Journal Entry Guide', component: KoutsuhiShiwakeEtcMeisaiGuidePage },
  { path: 'guides/etc-meisai-denshi-chobo-hozon-hou-guide', title: 'ETC明細と電子帳簿保存法ガイド', component: ETCMeisaiDenshiChoboHozonHouGuidePage },
  { path: 'guides/etc-statement-e-storage-law-guide', title: 'ETC Statement & e-Storage Act Guide', component: ETCMeisaiDenshiChoboHozonHouGuidePage },
  // Wave 2 / Iter 289 (2026-05-24): ETC明細 表示されない + 過去分 確認 (combined).
  { path: 'guides/etc-meisai-hyoji-sarenai-kako-guide', title: 'ETC利用明細が表示されない・過去分の確認ガイド', component: ETCMeisaiHyojiSarenaiKakoGuidePage },
  { path: 'guides/etc-statement-not-showing-historical-guide', title: 'ETC Statement Not Showing & Historical Records', component: ETCMeisaiHyojiSarenaiKakoGuidePage },
  // Wave 6 / Iter 290 (2026-05-24): 経費精算スターターキット cluster-hub.
  { path: 'guides/etc-keihi-seisan-starter-kit-guide', title: 'ETC利用記録 経費精算スターターキット', component: ETCKeihiSeisanStarterKitGuidePage },
  { path: 'guides/etc-expense-report-starter-kit-guide', title: 'ETC Expense Report Starter Kit', component: ETCKeihiSeisanStarterKitGuidePage },
  { path: 'etc-phishing-safety', title: 'ETC Safety', component: ETCPhishingSafetyPage },
  { path: 'etc-riyou-rireki-printer-guide', title: 'Printer Guide', component: ETCPrinterGuidePage },
  { path: 'etc-usage-history-printer-guide', title: 'Printer Guide', component: ETCPrinterGuidePage },
  { path: 'guides', title: 'Guides', component: GuidesHubPage },
  { path: 'guides/list', title: 'All Guides List', component: AllGuidesListPage },
  { path: 'toll-trip-status-checker', title: 'Did Your ETC Trip Post Yet?', component: TollTripStatusCheckerPage },
  { path: 'trust', title: 'Trust & Safety', component: TrustPage },
];

// Strips a leading `/ja` or `/en` prefix so we can compare two URLs
// for "same logical page, different language" equality. Used by
// ScrollToTop below to preserve the visitor's scroll position when
// they toggle the JA / EN button — the path technically changes
// (`/ja/foo` → `/en/foo`) but the content they were reading is the
// same, so jumping them back to the top is jarring and unwanted.
const LANG_PREFIX_RE = /^\/(ja|en)(?=\/|$)/;
const stripLangPrefix = (p) => (p || '').replace(LANG_PREFIX_RE, '') || '/';

function ScrollToTop() {
  const { pathname } = useLocation();
  // Track the previous pathname across renders so we can decide
  // whether the route change is a "real" navigation (different page)
  // or just a language toggle (same page, different prefix). Initial
  // mount writes the current value and skips the scrollTo so we
  // don't fight the browser's own scroll-restore behavior on
  // back / forward.
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    const prev = stripLangPrefix(prevPathRef.current);
    const next = stripLangPrefix(pathname);
    if (prev !== next) {
      // Force an INSTANT jump to the top, independent of any CSS
      // `scroll-behavior`. We temporarily pin `scroll-behavior: auto`
      // on <html> and use the plain two-arg scrollTo (no `behavior`
      // enum, which some browsers reject — making the whole call a
      // no-op and leaving the user stranded at the footer).
      const root = document.documentElement;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      // Restore on the next frame so genuine anchor smooth-scrolls (if any)
      // are unaffected.
      requestAnimationFrame(() => { root.style.scrollBehavior = prevBehavior; });
    }
    prevPathRef.current = pathname;
  }, [pathname]);

  // Immediate scroll-to-top on internal link clicks.
  //
  // React Router can take a noticeable beat to commit a new route on
  // heavy pages — during that gap the OLD (tall) page stays mounted, so
  // the effect above (which only runs after the commit) leaves the user
  // stranded at the footer for ~1s+. Jumping to the top synchronously on
  // the click itself makes navigation feel instant and also satisfies
  // "clicking any link should take me to the top". Anchor links (href
  // with '#'), new-tab, and external/mailto/tel links are left alone.
  useEffect(() => {
    const onClickCapture = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.target === '_blank') return;
      const href = a.getAttribute('href') || '';
      if (
        !href ||
        href.includes('#') ||
        /^(https?:|mailto:|tel:)/i.test(href)
      ) return;
      const root = document.documentElement;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      requestAnimationFrame(() => { root.style.scrollBehavior = prevBehavior; });
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, []);
  return null;
}

// Scroll reveal — applies fade-in / slide-up only to elements that
// explicitly opt in with the `data-reveal` attribute, so sections
// without it remain visible immediately.
function ScrollReveal() {
  useScrollReveal();
  return null;
}

function ScrollToTopButton() {
  const [show, setShow] = useState(false);
  const btnRef = useRef(null);
  const { pathname } = useLocation();
  // On admin routes the content is dense and operational; floating controls
  // over it (covering card text / actions) are not allowed on phones. Search
  // moves to the admin top bar there, so suppress this FAB below lg on /admin.
  const adminMobileSuppressed = (pathname || '').startsWith('/admin');
  // The private /design-preview reference build renders its own Swiss
  // primitives only — no legacy purple-branded floating controls.
  const designPreview = (pathname || '').startsWith('/design-preview');
  useEffect(() => {
    // rAF-throttle + passive scroll listener so the browser never
    // waits on JS to scroll. setShow only fires when the boundary
    // (400 px) is actually crossed — avoids per-frame React work.
    let ticking = false;
    let lastShow = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const next = window.scrollY > 400;
        if (next !== lastShow) {
          lastShow = next;
          setShow(next);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Never sit on top of an interactive content element at any scroll position.
  const colliding = useFloatingCollision(btnRef, { enabled: show });
  if (!show || designPreview) return null;
  return (
    <button
      ref={btnRef}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-[5.5rem] right-5 lg:bottom-5 z-[9999] w-11 h-11 rounded-full bg-[#9200C7] text-white shadow-lg hover:bg-[#7a00a8] transition-all items-center justify-center hover:-translate-y-1 ${adminMobileSuppressed ? 'hidden lg:flex' : 'flex'} ${colliding ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      data-testid="scroll-to-top"
      data-floating-control="true"
      aria-hidden={colliding ? 'true' : undefined}
      tabIndex={colliding ? -1 : undefined}
      aria-label="Scroll to top"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
    </button>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading-fallback">
      <div className="animate-spin h-8 w-8 border-2 border-[#9200C7] rounded-full border-t-transparent" />
    </div>
  );
}

// Mounts the intent-based hover/focus route-prefetch listener once (P2 perf).
// Renders nothing; warms light public lazy chunks on link hover. See
// lib/routePrefetch.js for the guardrails (desktop-only, Save-Data aware,
// excludes heavy guide-content + admin/dashboard chunks).
function HoverPrefetch() {
  useHoverPrefetch();
  return null;
}

// Fires the anonymous 'visitor' attribution stage once per session (privacy-safe).
function AttributionVisitor() {
  useEffect(() => { captureVisitor(); }, []);
  return null;
}

// Accessibility skip link, localized (WCAG 2.4.1 Bypass Blocks).
function SkipToContentLink() {
  const { lang } = useLanguage();
  return (
    <a href="#main-content" className="jtr-skip-to-content" data-testid="skip-to-content-link">
      {lang === 'ja' ? 'メインコンテンツへスキップ' : 'Skip to main content'}
    </a>
  );
}


function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AudienceProvider>
        <AuthProvider>
          <SmartTooltipProvider>
          {/* Accessibility: skip-to-content link — first focusable element on
              the page so keyboard / screen-reader users can jump past the
              header navigation directly to <main>. Visually hidden until
              focused (Tab key); see `.jtr-skip-to-content` in index.css.
              Pattern: WCAG 2.4.1 Bypass Blocks (Level A). Pages with a
              <main id="main-content"> element receive the jump; pages
              without one fall through to the default link behaviour. */}
          <SkipToContentLink />
          <LanguageSync />
          <LangPathEnforcer />
          <ScrollToTop />
          <MetaPixelRouter />
          <AttributionVisitor />
          <HoverPrefetch />
        <ScrollReveal />
          <ScrollToTopButton />
          <SEOHead />
          <ConditionalPublicAccessBanner />
          <EarlyAccessPopup />
          <VisitorBeacon />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ja" element={<HomePage />} />
            <Route path="/en" element={<HomePage />} />

            {/* Preview/test-ONLY MEISAI simulator harness. Not in any nav or
                sitemap. The backend `/api/sim/meisai/*` gate fails closed (404)
                in Production, so the page renders the 404 there. */}
            <Route path="/dev/sim/meisai" element={<SimulatorHarnessPage />} />
            {/* Preview-ONLY recurring-billing QA harness. Backend gate fails
                closed (404) on production build_env + a second host guard blocks
                the prod hosts, so this renders the 404 page in Production. */}
            <Route path="/dev/billing-qa" element={<BillingQaHarnessPage />} />
            {/* Private, non-indexed "Swiss & High-Contrast" design-system
                reference build (§6 Step 0 of the design proposal). Static
                synthetic-data-only primitive/state matrix — no real page is
                migrated yet. Own runtime host guard blocks Production hosts
                (renders 404 there) independent of the exclusions already in
                robots.txt / SEOHead / LangPathEnforcer / EarlyAccessPopup /
                VisitorBeacon / prerender.py's PUBLIC_PAGES allowlist. */}
            <Route path="/design-preview" element={<DesignPreviewPage />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/ja/login" element={<LoginPage />} />
            <Route path="/en/login" element={<LoginPage />} />
            {/* /signin aliases — redirect to canonical /login. Added
                after iter 75 regression sweep flagged that inbound
                deeplinks / external sign-in CTAs land on 404. */}
            <Route path="/signin" element={<Navigate to="/login" replace />} />
            <Route path="/ja/signin" element={<Navigate to="/ja/login" replace />} />
            <Route path="/en/signin" element={<Navigate to="/en/login" replace />} />
            <Route path="/federation/merge" element={<FederationMergeConfirmPage />} />
            <Route path="/sso" element={<SsoRedeemPage />} />
            <Route path="/ja/sso" element={<SsoRedeemPage />} />
            <Route path="/en/sso" element={<SsoRedeemPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/ja/register" element={<RegisterPage />} />
            <Route path="/en/register" element={<RegisterPage />} />
            {/* `/signup` is a natural English keyword & high-intent JA
                /SEO landing target — redirect to `/register` rather than
                404. (Iter 245-final 2026-05-23.) */}
            <Route path="/signup" element={<Navigate to="/register" replace />} />
            <Route path="/ja/signup" element={<Navigate to="/ja/register" replace />} />
            <Route path="/en/signup" element={<Navigate to="/en/register" replace />} />
            {/* Locale-prefixed dashboard links resolve to the (locale-agnostic)
                authenticated dashboard instead of falling through to the 404. */}
            <Route path="/ja/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/en/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/ja/dashboard/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/en/dashboard/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/org-invite/accept" element={<OrgInviteAcceptPage />} />
            <Route path="/ja/org-invite/accept" element={<OrgInviteAcceptPage />} />
            <Route path="/en/org-invite/accept" element={<OrgInviteAcceptPage />} />

            {/* Early Access waitlist (public, pre-launch email capture) */}
            <Route path="/early-access" element={<EarlyAccessLandingPage />} />
            <Route path="/ja/early-access" element={<EarlyAccessLandingPage />} />
            <Route path="/en/early-access" element={<EarlyAccessLandingPage />} />
            {/* Dedicated ad/campaign landing page (Facebook/Google traffic) —
                reuses the same Early Access list + UTM capture. */}
            {/* Ad Creative Review console — rendered by the SPA (the production
                static host doesn't resolve directory-index HTML for clean paths,
                so static /ja/ad-creatives/index.html cannot be served there). */}
            <Route path="/ad-creatives" element={<Navigate to="/ja/ad-creatives" replace />} />
            <Route path="/ja/ad-creatives" element={<AdCreativesConsolePage lang="ja" />} />
            <Route path="/en/ad-creatives" element={<AdCreativesConsolePage lang="en" />} />
            <Route path="/early-access-ad" element={<AdLandingPage />} />
            <Route path="/ja/early-access-ad" element={<AdLandingPage />} />
            <Route path="/en/early-access-ad" element={<AdLandingPage />} />
            {/* Audience-targeted ad variants — same waitlist + UTM, pre-selected
                account type + audience-specific messaging/SEO content. */}
            <Route path="/early-access-personal" element={<AdLandingPage audience="personal" />} />
            <Route path="/ja/early-access-personal" element={<AdLandingPage audience="personal" />} />
            <Route path="/en/early-access-personal" element={<AdLandingPage audience="personal" />} />
            <Route path="/early-access-business" element={<AdLandingPage audience="business" />} />
            <Route path="/ja/early-access-business" element={<AdLandingPage audience="business" />} />
            <Route path="/en/early-access-business" element={<AdLandingPage audience="business" />} />
            <Route path="/early-access-government" element={<AdLandingPage audience="government" />} />
            <Route path="/ja/early-access-government" element={<AdLandingPage audience="government" />} />
            <Route path="/en/early-access-government" element={<AdLandingPage audience="government" />} />
            <Route path="/early-access/confirm" element={<EarlyAccessConfirmPage />} />
            <Route path="/ja/early-access/confirm" element={<EarlyAccessConfirmPage />} />
            <Route path="/en/early-access/confirm" element={<EarlyAccessConfirmPage />} />
            <Route path="/email/preferences" element={<EmailPreferencesPage />} />
            <Route path="/ja/email/preferences" element={<EmailPreferencesPage />} />
            <Route path="/en/email/preferences" element={<EmailPreferencesPage />} />

            {publicPages.flatMap(({ path, title, component: Comp }) => {
              const el = Comp ? <Comp /> : <PP title={title} />;
              return [
                <Route key={path} path={`/${path}`} element={el} />,
                <Route key={`ja-${path}`} path={`/ja/${path}`} element={el} />,
                <Route key={`en-${path}`} path={`/en/${path}`} element={el} />,
              ];
            })}

            <Route path="/terms" element={<TermsPage />} />
            <Route path="/ja/terms" element={<TermsPage />} />
            <Route path="/en/terms" element={<TermsPage />} />
            <Route path="/official-etc-resources" element={<OfficialResourcesPage />} />
            <Route path="/ja/official-etc-resources" element={<OfficialResourcesPage />} />
            <Route path="/en/official-etc-resources" element={<OfficialResourcesPage />} />
            {/* ── Government & Military toll cluster — RESTORED 2026-06 ──
                Original pre-refactor pages reinstated (forward, no rollback):
                US-military/SOFA toll page, MOFA/diplomatic, official convoys,
                Diet members, police/fire/emergency, the records hub, and the
                neutral reference pages. */}
            <Route path="/government-military-toll" element={<GovernmentMilitaryTollPage />} />
            <Route path="/ja/government-military-toll" element={<GovernmentMilitaryTollPage />} />
            <Route path="/en/government-military-toll" element={<GovernmentMilitaryTollPage />} />
            {/* Canonical Resources & References doorway */}
            <Route path="/government-vehicle-toll-resources-japan" element={<GovVehicleResourcesPage />} />
            <Route path="/ja/government-vehicle-toll-resources-japan" element={<GovVehicleResourcesPage />} />
            <Route path="/en/government-vehicle-toll-resources-japan" element={<GovVehicleResourcesPage />} />
            <Route path="/government-official-vehicle-resources-japan" element={<Navigate replace to="/government-vehicle-toll-resources-japan" />} />
            <Route path="/ja/government-official-vehicle-resources-japan" element={<Navigate replace to="/ja/government-vehicle-toll-resources-japan" />} />
            <Route path="/en/government-official-vehicle-resources-japan" element={<Navigate replace to="/en/government-vehicle-toll-resources-japan" />} />
            <Route path="/government-military-toll-records-japan" element={<GovTollHubPage />} />
            <Route path="/ja/government-military-toll-records-japan" element={<GovTollHubPage />} />
            <Route path="/en/government-military-toll-records-japan" element={<GovTollHubPage />} />
            <Route path="/government-official-vehicle-tolls-japan" element={<GovOfficialVehicleTollsPage />} />
            <Route path="/ja/government-official-vehicle-tolls-japan" element={<GovOfficialVehicleTollsPage />} />
            <Route path="/en/government-official-vehicle-tolls-japan" element={<GovOfficialVehicleTollsPage />} />
            <Route path="/japanese-police-fire-emergency-vehicle-toll-rules" element={<PoliceFireEmergencyTollPage />} />
            <Route path="/ja/japanese-police-fire-emergency-vehicle-toll-rules" element={<PoliceFireEmergencyTollPage />} />
            <Route path="/en/japanese-police-fire-emergency-vehicle-toll-rules" element={<PoliceFireEmergencyTollPage />} />
            <Route path="/japanese-official-convoy-toll-payments" element={<OfficialConvoyTollPage />} />
            <Route path="/ja/japanese-official-convoy-toll-payments" element={<OfficialConvoyTollPage />} />
            <Route path="/en/japanese-official-convoy-toll-payments" element={<OfficialConvoyTollPage />} />
            <Route path="/diet-members-official-travel-tolls-japan" element={<DietMembersTollPage />} />
            <Route path="/ja/diet-members-official-travel-tolls-japan" element={<DietMembersTollPage />} />
            <Route path="/en/diet-members-official-travel-tolls-japan" element={<DietMembersTollPage />} />
            <Route path="/who-pays-government-vehicle-tolls-japan" element={<WhoPaysGovVehicleTollsPage />} />
            <Route path="/ja/who-pays-government-vehicle-tolls-japan" element={<WhoPaysGovVehicleTollsPage />} />
            <Route path="/en/who-pays-government-vehicle-tolls-japan" element={<WhoPaysGovVehicleTollsPage />} />
            <Route path="/government-fleet-etc-recordkeeping-japan" element={<GovFleetRecordkeepingPage />} />
            <Route path="/ja/government-fleet-etc-recordkeeping-japan" element={<GovFleetRecordkeepingPage />} />
            <Route path="/en/government-fleet-etc-recordkeeping-japan" element={<GovFleetRecordkeepingPage />} />
            <Route path="/mofa-diplomatic-official-vehicle-tolls-japan" element={<MofaDiplomaticVehicleTollsPage />} />
            <Route path="/ja/mofa-diplomatic-official-vehicle-tolls-japan" element={<MofaDiplomaticVehicleTollsPage />} />
            <Route path="/en/mofa-diplomatic-official-vehicle-tolls-japan" element={<MofaDiplomaticVehicleTollsPage />} />
            {/* USFJ SOFA alias → canonical /government-military-toll */}
            <Route path="/usfj-sofa-toll-certificates-japan" element={<Navigate to="/government-military-toll" replace />} />
            <Route path="/ja/usfj-sofa-toll-certificates-japan" element={<Navigate to="/ja/government-military-toll" replace />} />
            <Route path="/en/usfj-sofa-toll-certificates-japan" element={<Navigate to="/en/government-military-toll" replace />} />
            {/* Neutral public-safety records page kept reachable (added 2026-06) */}
            <Route path="/public-safety-fleet-etc-records-japan" element={<PublicSafetyFleetRecordsPage />} />
            <Route path="/ja/public-safety-fleet-etc-records-japan" element={<PublicSafetyFleetRecordsPage />} />
            <Route path="/en/public-safety-fleet-etc-records-japan" element={<PublicSafetyFleetRecordsPage />} />
            {/* Restored guide articles (military-government, SOFA drivers EN+JA,
                Yokosuka EN + Beigun-Yokosuka JA) are served by the SEO landing
                system via SEO_LANDING_PAGE_SLUGS below. */}
            <Route path="/why" element={<WhyJapanTollReceiptsPage />} />
            <Route path="/ja/why" element={<WhyJapanTollReceiptsPage />} />
            <Route path="/en/why" element={<WhyJapanTollReceiptsPage />} />
            {/* Legacy keyword URL — redirect to the canonical /why (which has the
                SEOHead metadata + internal links). Prevents a stray sitemap 404. */}
            <Route path="/why-japan-toll-receipts" element={<Navigate replace to="/why" />} />
            <Route path="/ja/why-japan-toll-receipts" element={<Navigate replace to="/ja/why" />} />
            <Route path="/en/why-japan-toll-receipts" element={<Navigate replace to="/en/why" />} />
            {/* Engineering Trust Center — single consolidated page.
                Old sub-routes redirect to the matching in-page anchor. */}
            {[
              ['architecture', ''],
              ['architecture/safety-governor', '#governor'],
              ['architecture/queue-and-scheduling', '#queue'],
              ['architecture/private-workers', '#workers'],
              ['architecture/data-integrity', '#integrity'],
              ['architecture/scaling-model', '#scaling'],
              ['architecture/faq', '#faq'],
            ].flatMap(([path, hash]) => {
              const el = hash
                ? <Navigate to={`/architecture${hash}`} replace />
                : <ArchitecturePage />;
              const elJa = hash
                ? <Navigate to={`/ja/architecture${hash}`} replace />
                : <ArchitecturePage />;
              const elEn = hash
                ? <Navigate to={`/en/architecture${hash}`} replace />
                : <ArchitecturePage />;
              return [
                <Route key={path} path={`/${path}`} element={el} />,
                <Route key={`ja-${path}`} path={`/ja/${path}`} element={elJa} />,
                <Route key={`en-${path}`} path={`/en/${path}`} element={elEn} />,
              ];
            })}
            {/* Registered Vehicle Monitoring is a rich custom feature page;
                it lives under /features/... like every other feature page.
                Iter 132 — merged into the feature catalog per customer
                request, with 301-style redirects from the legacy URL. */}
            <Route path="/features/authorized-vehicle-monitoring" element={<AuthorizedVehicleMonitoringPage />} />
            <Route path="/ja/features/authorized-vehicle-monitoring" element={<AuthorizedVehicleMonitoringPage />} />
            <Route path="/en/features/authorized-vehicle-monitoring" element={<AuthorizedVehicleMonitoringPage />} />
            {/* Legacy URL redirects — preserve old links/bookmarks/search snippets. */}
            <Route path="/authorized-vehicle-monitoring" element={<Navigate to="/features/authorized-vehicle-monitoring" replace />} />
            <Route path="/ja/authorized-vehicle-monitoring" element={<Navigate to="/ja/features/authorized-vehicle-monitoring" replace />} />
            <Route path="/en/authorized-vehicle-monitoring" element={<Navigate to="/en/features/authorized-vehicle-monitoring" replace />} />
            <Route path="/personal/family-vehicle-list" element={<FamilyVehicleListPage />} />
            <Route path="/ja/personal/family-vehicle-list" element={<FamilyVehicleListPage />} />
            <Route path="/en/personal/family-vehicle-list" element={<FamilyVehicleListPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/ja/privacy" element={<PrivacyPage />} />
            <Route path="/en/privacy" element={<PrivacyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/ja/disclaimer" element={<DisclaimerPage />} />
            <Route path="/en/disclaimer" element={<DisclaimerPage />} />
            <Route path="/consent-policy" element={<ConsentPolicyPage />} />
            <Route path="/ja/consent-policy" element={<ConsentPolicyPage />} />
            <Route path="/en/consent-policy" element={<ConsentPolicyPage />} />
            <Route path="/data-handling" element={<DataHandlingPage />} />
            <Route path="/ja/data-handling" element={<DataHandlingPage />} />
            <Route path="/en/data-handling" element={<DataHandlingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/ja/about" element={<AboutPage />} />
            <Route path="/en/about" element={<AboutPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/ja/changelog" element={<ChangelogPage />} />
            <Route path="/en/changelog" element={<ChangelogPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/ja/roadmap" element={<RoadmapPage />} />
            <Route path="/en/roadmap" element={<RoadmapPage />} />
            <Route path="/live-reporting" element={<LiveTestingPage />} />
            <Route path="/ja/live-reporting" element={<LiveTestingPage />} />
            <Route path="/en/live-reporting" element={<LiveTestingPage />} />
            {/* Legacy "Live Testing" slug → permanent "Live Reporting" rebrand.
                Client redirect with noindex,follow so any indexed /live-testing
                consolidates its link-equity onto the canonical /live-reporting. */}
            <Route path="/live-testing" element={<LegacyRedirect to="/live-reporting" />} />
            <Route path="/ja/live-testing" element={<LegacyRedirect to="/ja/live-reporting" />} />
            <Route path="/en/live-testing" element={<LegacyRedirect to="/en/live-reporting" />} />
            <Route path="/legal/commercial-transactions" element={<CommercialTransactionsPage />} />
            <Route path="/ja/legal/commercial-transactions" element={<CommercialTransactionsPage />} />
            <Route path="/en/legal/commercial-transactions" element={<CommercialTransactionsPage />} />
            <Route path="/legal/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/ja/legal/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/en/legal/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/market" element={<MarketOpportunityPage />} />
            <Route path="/:lang/market" element={<MarketOpportunityPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/ja/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/en/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/account/recovery" element={<AccountRecoveryPage />} />
            <Route path="/recovery/meisai" element={<RecoveryMeisaiPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/ja/team" element={<TeamPage />} />
            <Route path="/en/team" element={<TeamPage />} />
            <Route path="/our-team" element={<TeamPage />} />
            <Route path="/ja/our-team" element={<TeamPage />} />
            <Route path="/en/our-team" element={<TeamPage />} />
            {/* Founder's Message — primary URL */}
            <Route path="/founders-message" element={<OurStoryPage />} />
            <Route path="/ja/founders-message" element={<OurStoryPage />} />
            <Route path="/en/founders-message" element={<OurStoryPage />} />
            {/* Legacy /our-story alias — keep for any existing inbound links */}
            <Route path="/our-story" element={<OurStoryPage />} />
            <Route path="/ja/our-story" element={<OurStoryPage />} />
            <Route path="/en/our-story" element={<OurStoryPage />} />
            {/* Corporate Profile — operated-by NoJo Enterprise trust page */}
            {/* Short, partner-friendly alias → canonical /corporate-profile (avoids 404, no duplicate content) */}
            <Route path="/corporate" element={<Navigate replace to="/corporate-profile" />} />
            <Route path="/ja/corporate" element={<Navigate replace to="/ja/corporate-profile" />} />
            <Route path="/en/corporate" element={<Navigate replace to="/en/corporate-profile" />} />
            <Route path="/corporate-profile" element={<CorporateProfilePage />} />
            <Route path="/ja/corporate-profile" element={<CorporateProfilePage />} />
            <Route path="/en/corporate-profile" element={<CorporateProfilePage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/ja/testimonials" element={<TestimonialsPage />} />
            <Route path="/en/testimonials" element={<TestimonialsPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/ja/sitemap" element={<SitemapPage />} />
            <Route path="/en/sitemap" element={<SitemapPage />} />
            <Route path="/guides/etc-card-number-formats" element={<ETCCardNumberFormatsPage />} />
            <Route path="/ja/guides/etc-card-number-formats" element={<ETCCardNumberFormatsPage />} />
            <Route path="/en/guides/etc-card-number-formats" element={<ETCCardNumberFormatsPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/ja/policies" element={<PoliciesPage />} />
            <Route path="/en/policies" element={<PoliciesPage />} />
            <Route path="/help" element={<Navigate to="/contact" replace />} />
            <Route path="/ja/help" element={<Navigate to="/ja/contact" replace />} />
            <Route path="/en/help" element={<Navigate to="/en/contact" replace />} />
            <Route path="/email-change/revert" element={<EmailChangeRevertPage />} />

            {/* ─── Iter 156 SEO Keyword Landing Pages (under /guides/* only) ─
                Each `SEO_LANDING_PAGES` entry generates 3 routes:
                  /guides/<slug>            (default lang resolves at runtime)
                  /ja/guides/<slug>         (Japanese variant)
                  /en/guides/<slug>         (English variant)
                Old root-level / namespaced routes (e.g.
                `/ja/etc-riyoumeisai-guide` or
                `/en/business/etc-fleet-toll-reporting`) are redirected
                below to keep any inbound link / index pointing valid. */}
            {SEO_LANDING_PAGE_SLUGS.flatMap((slug) => [
              <Route key={`seo-${slug}`} path={`/guides/${slug}`} element={<SuspendedSeoLandingPage forcedSlug={slug} />} />,
              <Route key={`seo-ja-${slug}`} path={`/ja/guides/${slug}`} element={<SuspendedSeoLandingPage forcedSlug={slug} />} />,
              <Route key={`seo-en-${slug}`} path={`/en/guides/${slug}`} element={<SuspendedSeoLandingPage forcedSlug={slug} />} />,
              // Backwards-compatible redirects from the Iter 155 routes
              <Route key={`seo-redirect-${slug}`} path={`/${slug}`} element={<Navigate replace to={`/guides/${slug}`} />} />,
              <Route key={`seo-redirect-ja-${slug}`} path={`/ja/${slug}`} element={<Navigate replace to={`/ja/guides/${slug}`} />} />,
              <Route key={`seo-redirect-en-${slug}`} path={`/en/${slug}`} element={<Navigate replace to={`/en/guides/${slug}`} />} />,
            ])}
            {/* Legacy slashed slugs from Iter 155 → flattened equivalents */}
            <Route path="/business/etc-fleet-toll-reporting" element={<Navigate replace to="/guides/business-etc-fleet-toll-reporting" />} />
            <Route path="/business/corporate-etc-card-management" element={<Navigate replace to="/guides/corporate-etc-card-management" />} />
            <Route path="/government/military-etc-card-reporting" element={<Navigate replace to="/guides/military-government-etc-card-reporting" />} />
            <Route path="/ja/business/etc-fleet-toll-reporting" element={<Navigate replace to="/ja/guides/business-etc-fleet-toll-reporting" />} />
            <Route path="/ja/business/corporate-etc-card-management" element={<Navigate replace to="/ja/guides/corporate-etc-card-management" />} />
            <Route path="/ja/government/military-etc-card-reporting" element={<Navigate replace to="/ja/guides/military-government-etc-card-reporting" />} />
            <Route path="/en/business/etc-fleet-toll-reporting" element={<Navigate replace to="/en/guides/business-etc-fleet-toll-reporting" />} />
            <Route path="/en/business/corporate-etc-card-management" element={<Navigate replace to="/en/guides/corporate-etc-card-management" />} />
            <Route path="/en/government/military-etc-card-reporting" element={<Navigate replace to="/en/guides/military-government-etc-card-reporting" />} />

            {/* Catch-all for malformed / stale guide URLs. An old link such as
                /en/guides/Issuer%20websites (accidentally generated from a
                descriptive label, then indexed by Google) used to hit the
                global 404. Any /guides/* path that does NOT match a real guide
                above now redirects to the guides hub, so inbound/indexed links
                resolve to a valid page instead of erroring. Real guide routes
                are static and rank higher in React Router, so they are
                unaffected by this splat fallback. */}
            <Route path="/guides/*" element={<Navigate replace to="/guides" />} />
            <Route path="/ja/guides/*" element={<Navigate replace to="/ja/guides" />} />
            <Route path="/en/guides/*" element={<Navigate replace to="/en/guides" />} />

            {/* Business Suite — public landing */}
            {/* /business-suite — Phase 1 landing page restored.
                Public marketing surface for the Business Suite plan;
                the in-app apply form lives at /dashboard/business-suite/apply
                behind login. */}
            <Route path="/business-suite" element={<BusinessSuiteLandingPage />} />
            <Route path="/ja/business-suite" element={<BusinessSuiteLandingPage />} />
            <Route path="/en/business-suite" element={<BusinessSuiteLandingPage />} />

            {/* /business-suite/apply — public alias. Unauthenticated visitors
                must be funneled through registration with intent=business so
                they land on the in-app apply form post-signup. Previously
                fell through to the catch-all and silently sent users to '/',
                losing the business-application intent. (iter 71 fix) */}
            <Route
              path="/business-suite/apply"
              element={<Navigate to="/register?intent=business&next=/dashboard/business-suite/apply" replace />}
            />
            <Route
              path="/ja/business-suite/apply"
              element={<Navigate to="/ja/register?intent=business&next=/dashboard/business-suite/apply" replace />}
            />
            <Route
              path="/en/business-suite/apply"
              element={<Navigate to="/en/register?intent=business&next=/dashboard/business-suite/apply" replace />}
            />

            {/* Free Weekly dedicated page — always-free, up to 10 cards */}
            <Route path="/free-weekly" element={<FreeWeeklyPlanPage />} />
            <Route path="/ja/free-weekly" element={<FreeWeeklyPlanPage />} />
            <Route path="/en/free-weekly" element={<FreeWeeklyPlanPage />} />

            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<DashboardErrorBoundary><AdaptiveDashboard /></DashboardErrorBoundary>} />
              <Route path="overview" element={<DashboardErrorBoundary><AdaptiveDashboard /></DashboardErrorBoundary>} />
              <Route path="advanced" element={<DashboardErrorBoundary><DashboardOverview /></DashboardErrorBoundary>} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="card-mapping" element={<CardMappingPage />} />
              <Route path="recipients" element={<RecipientsPage />} />
              <Route path="integrations/japanetccard" element={<JapanETCcardSyncPage />} />
              <Route path="card-recipients" element={<Navigate to="/dashboard/recipients" replace />} />
              <Route path="card-registration-status" element={<CardRegistrationStatusPage />} />
              <Route path="setup" element={<CardsPage />} />
              <Route path="meisai-setup" element={<MeisaiSetupPage />} />
              <Route path="reports" element={<ReportsHubPage />} />
              <Route path="reports/delivery" element={<DeliverySettingsPage />} />
              <Route path="reports/history" element={<ReportsPage />} />
              <Route path="custom-reports" element={<EntitlementGate feature="custom_date_reports"><CustomReportsPage /></EntitlementGate>} />
              <Route path="reimbursement" element={<EntitlementGate feature="reimbursement_reports"><ReimbursementPage /></EntitlementGate>} />
              <Route path="vehicle-monitoring" element={<EntitlementGate feature="vehicle_monitoring"><VehicleMonitoringPage /></EntitlementGate>} />
              <Route path="meisai-health" element={<EntitlementGate feature="meisai_connection_health"><MeisaiHealthPage /></EntitlementGate>} />
              <Route path="meisai-vault" element={<EntitlementGate feature="meisai_connection_health"><MeisaiVaultPage /></EntitlementGate>} />
              <Route path="early-access" element={<EarlyAccessPage />} />
              <Route path="plan" element={<PlanPage />} />
              <Route path="ai-review" element={<EntitlementGate feature="ai_pattern_review"><AIReviewPage /></EntitlementGate>} />
              <Route path="manager-review" element={<EntitlementGate feature="manager_review_queue"><ManagerReviewQueuePage /></EntitlementGate>} />
              <Route path="reports/request" element={<ReportsPage />} />
              <Route path="schedules" element={<SchedulesPage />} />
              {/* PM97 alias — avoid a jarring in-dashboard 404 if a customer
                  reaches /dashboard/scheduled-reports (the personal schedules
                  UI lives at /dashboard/schedules). */}
              <Route path="scheduled-reports" element={<Navigate to="/dashboard/schedules" replace />} />
              <Route path="billing" element={<BillingPreviewPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="close-account" element={<AccountClosurePage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="cc-emails" element={<Navigate to="/dashboard/recipients" replace />} />
              <Route path="consent" element={<Navigate to="/dashboard/settings" replace />} />
              <Route path="referrals" element={<ReferralsPage />} />
              {/* Iter 331 — Business Suite shell + sub-pages now gated by
                  `business_suite_access` so a Personal user typing the
                  URL directly hits the bilingual RestrictedAccess page
                  instead of seeing fleet UI. /business-suite/apply is
                  intentionally *not* gated so Personal users can apply
                  to upgrade to the suite. */}
              <Route path="business-suite" element={<EntitlementGate feature="business_suite_access"><BusinessSuiteDashboardPage /></EntitlementGate>} />
              <Route path="business-suite/apply" element={<BusinessSuiteApplyPage />} />
              <Route path="business-suite/meisai" element={<EntitlementGate feature="business_suite_access"><BusinessSuiteMeisaiPage /></EntitlementGate>} />
              <Route path="business-suite/cards" element={<EntitlementGate feature="business_suite_access"><BusinessSuiteCardsPage /></EntitlementGate>} />
              <Route path="business-suite/wizard" element={<EntitlementGate feature="business_suite_access"><MeisaiAccountWizard /></EntitlementGate>} />
              <Route path="business-suite/card-jobs" element={<EntitlementGate feature="business_suite_access"><CardJobFlowPage /></EntitlementGate>} />
              <Route path="business-suite/cards-hub" element={<EntitlementGate feature="business_suite_access"><CardManagementHubPage /></EntitlementGate>} />
              <Route path="business-suite/bulk-import" element={<EntitlementGate feature="business_suite_access"><BulkImportWizardPage /></EntitlementGate>} />
              <Route path="business-suite/card-billing" element={<EntitlementGate feature="business_suite_access"><CardBillingPreviewPage /></EntitlementGate>} />
              <Route path="business-suite/vehicles" element={<EntitlementGate feature="business_suite_access"><VehicleManagementPage /></EntitlementGate>} />
              <Route path="business-suite/vehicle-import" element={<EntitlementGate feature="business_suite_access"><VehicleBulkImportPage /></EntitlementGate>} />
              <Route path="business-suite/red-flags" element={<EntitlementGate feature="business_suite_access"><RedFlagDashboardPage /></EntitlementGate>} />
              <Route path="business-suite/scheduled-reports" element={<EntitlementGate feature="business_suite_access"><ScheduledReportsPage /></EntitlementGate>} />
              <Route path="business-suite/operations" element={<EntitlementGate feature="business_suite_access"><OperationsDashboardPage /></EntitlementGate>} />
              <Route path="business-suite/group-manager" element={<EntitlementGate feature="business_suite_access"><GroupManagerWorkspacePage /></EntitlementGate>} />
              <Route path="business-suite/queues" element={<EntitlementGate feature="business_suite_access"><QueueCenterPage /></EntitlementGate>} />
              <Route path="business-suite/notifications" element={<EntitlementGate feature="business_suite_access"><NotificationCenterPage /></EntitlementGate>} />
              <Route path="business-suite/hierarchy" element={<EntitlementGate feature="business_suite_access"><GroupHierarchyPage /></EntitlementGate>} />
              <Route path="business-suite/fleet-activation" element={<EntitlementGate feature="business_suite_access"><FleetActivationPage /></EntitlementGate>} />
              <Route path="business-suite/activation-queue" element={<EntitlementGate feature="business_suite_access"><ActivationBlockerQueuePage /></EntitlementGate>} />
              <Route path="business-suite/card-readiness" element={<EntitlementGate feature="business_suite_access"><CardReadinessPage /></EntitlementGate>} />
              <Route path="business-suite/report-transparency" element={<EntitlementGate feature="business_suite_access"><ReportTransparencyPage /></EntitlementGate>} />
              <Route path="business-suite/notifications-visibility" element={<EntitlementGate feature="business_suite_access"><NotificationVisibilityPage /></EntitlementGate>} />
              <Route path="business-suite/track-overview" element={<EntitlementGate feature="business_suite_access"><TrackOverviewPage /></EntitlementGate>} />
              <Route path="business-suite/government-oversight" element={<EntitlementGate feature="business_suite_access"><TrackOverviewPage /></EntitlementGate>} />
              <Route path="business-suite/business-fleet" element={<EntitlementGate feature="business_suite_access"><TrackOverviewPage /></EntitlementGate>} />
              <Route path="business-suite/vehicle-readiness" element={<EntitlementGate feature="business_suite_access"><VehicleReadinessPage /></EntitlementGate>} />
              <Route path="business-suite/account-manager" element={<EntitlementGate feature="business_suite_access"><AccountManagerPage /></EntitlementGate>} />
              <Route path="business-suite/report-center" element={<EntitlementGate feature="business_suite_access"><ReportOnlyDashboardPage /></EntitlementGate>} />
              <Route path="business-suite/card/:cardId" element={<EntitlementGate feature="business_suite_access"><CardDetailPage /></EntitlementGate>} />
              <Route path="business-suite/vehicle/:vehicleId" element={<EntitlementGate feature="business_suite_access"><VehicleDetailPage /></EntitlementGate>} />
              <Route path="business-suite/groups" element={<EntitlementGate feature="business_suite_access"><BusinessSuiteGroupsPage /></EntitlementGate>} />
              <Route path="business-suite/group-management" element={<EntitlementGate feature="business_suite_access"><OrgGroupManagementPage /></EntitlementGate>} />
              <Route path="business-suite/reports" element={<EntitlementGate feature="bulk_reports"><BusinessSuiteBulkReportsPage /></EntitlementGate>} />
              <Route path="business-suite/alerts" element={<EntitlementGate feature="business_suite_access"><BusinessSuiteAlertsPage /></EntitlementGate>} />
              {/* Stack B legacy route → unified onto the canonical org_vehicles registry (Phase 1A). */}
              <Route path="business-suite/authorized-vehicles" element={<Navigate to="/dashboard/business-suite/vehicles" replace />} />
              <Route path="spend-alerts" element={<EntitlementGate feature="smart_spend_alerts"><SpendAlertsPage /></EntitlementGate>} />
              <Route path="family-authorized-vehicles" element={<EntitlementGate feature="family_vehicle_list"><FamilyAuthorizedVehiclesPage /></EntitlementGate>} />
              <Route path="self-rescue" element={<SelfRescuePage />} />
              <Route path="*" element={<DashboardNotFound variant="customer" />} />
            </Route>

            {/* Owner may reach the acceptance runner via the locale-prefixed URL
                referenced in the go-live steps; admin routes are unprefixed, so
                alias /en|/ja /admin/production-acceptance → /admin/production-acceptance. */}
            <Route path="/:lang/admin/production-acceptance" element={<Navigate to="/admin/production-acceptance" replace />} />

            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminThemeProvider><DashboardLayout /></AdminThemeProvider></ProtectedRoute>}>
              <Route index element={<AdminCommandCenter />} />
              <Route path="metrics" element={<AdminOverview />} />
              <Route path="issues" element={<AdminIssuesPage />} />
              <Route path="customers" element={<AdminCustomersPage />} />
              <Route path="closed-accounts" element={<AdminClosedAccountsPage />} />
              <Route path="registration-attempts" element={<AdminRegistrationAttemptsPage />} />
              <Route path="fraud-watch" element={<AdminFraudWatchPage />} />
              <Route path="customers/:customerId/360" element={<AdminCustomer360Page />} />
              <Route path="customers/:customerId" element={<AdminCustomer360Page />} />
              <Route path="diagnostic" element={<AdminDiagnosticPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="quarantine" element={<AdminQuarantinePage />} />
              <Route path="federation" element={<AdminFederationPage />} />
              <Route path="federation/diagnose" element={<AdminFederationDiagnosePage />} />
              <Route path="sync-status" element={<AdminSyncStatusPage />} />
              <Route path="jobs" element={<AdminJobMonitorPage />} />
              <Route path="scheduler-throughput" element={<AdminSchedulerThroughputPage />} />
              <Route path="launch-status" element={<AdminLaunchStatusPage />} />
              <Route path="performance" element={<AdminPerformanceScalePage />} />
              <Route path="governor" element={<AdminGovernorPage />} />
              <Route path="release-log" element={<AdminReleaseLogPage />} />
              <Route path="audit" element={<AdminAuditLogsPage />} />
              <Route path="export-audit" element={<AdminExportAuditPage />} />
              <Route path="marketing-command-center" element={<MarketingCommandCenterPage />} />
              <Route path="account-type-requests" element={<AdminAccountTypeRequestsPage />} />
              <Route path="emails" element={<DP title="Email Logs" />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="workflow-playbook" element={<AdminWorkflowPlaybookPage />} />
              <Route path="launch-checklist" element={<AdminLaunchChecklist />} />
              <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
              <Route path="early-access" element={<AdminEarlyAccessPage />} />
              <Route path="email-campaigns" element={<AdminEmailCampaignsPage />} />
              <Route path="og-preview" element={<AdminOgPreviewPage />} />
              <Route path="beta-operations" element={<AdminBetaOperationsPage />} />
              <Route path="automation" element={<AdminAutomationCenterPage />} />
              <Route path="mission-control" element={<AdminMissionControlPage />} />
              <Route path="launch-week-ops" element={<AdminLaunchWeekOpsPage />} />
              <Route path="security-events" element={<AdminSecurityEventsPage />} />
              <Route path="security" element={<AdminSecurityCockpitPage />} />
              <Route path="scheduler-health" element={<AdminSchedulerHealthPage />} />
              <Route path="production-doctor" element={<AdminProductionDoctorPage />} />
              <Route path="production-acceptance" element={<AdminProductionAcceptancePage />} />
              <Route path="report-simulator" element={<AdminReportSimulatorPage />} />
              <Route path="report-activity" element={<AdminReportActivityPage />} />
              <Route path="data-health" element={<AdminDataHealthPage />} />
              <Route path="meisai-monitor" element={<AdminMeisaiMonitorPage />} />
              <Route path="org-operations" element={<AdminOrgOperationsPage />} />
              <Route path="org-operations/:orgId" element={<AdminOrgDetailPage />} />
              <Route path="phase-b-live" element={<AdminPhaseBLivePage />} />
              <Route path="meisai-live-accounts" element={<AdminMeisaiTestAccountsPage />} />
              <Route path="email-delivery-health" element={<AdminEmailDeliveryHealthPage />} />
              <Route path="host-tools" element={<AdminHostToolsPage />} />
              <Route path="exports" element={<AdminExportsPage />} />
              <Route path="support-messages" element={<AdminSupportMessagesPage />} />
              <Route path="self-rescue-requests" element={<AdminSelfRescuePage />} />
              <Route path="abuse-review" element={<AdminAbuseReviewPage />} />
              <Route path="card-billing" element={<AdminCardBillingPage />} />
              <Route path="business-applications" element={<AdminBusinessApplicationsPage />} />
              <Route path="report-failures" element={<AdminReportFailuresPage />} />
              <Route path="failure-recovery" element={<AdminFailureRecoveryPage />} />
              <Route path="error-intelligence" element={<AdminErrorIntelligencePage />} />
              <Route path="visitors" element={<AdminVisitorsPage />} />
              <Route path="visitors/v/:visitorId" element={<AdminVisitorDetailPage />} />
              <Route path="testimonial-translations" element={<AdminTestimonialTranslationsPage />} />
              <Route path="billing-readiness" element={<AdminBillingReadinessPage />} />
              <Route path="homepage-media" element={<AdminHomepageMediaPage />} />
              <Route path="*" element={<DashboardNotFound variant="admin" />} />
            </Route>

            {/* ── Iter 64: IA restructure — Personal / Business tracks ── */}
            <Route path="/personal" element={<PersonalOverviewPage />} />
            <Route path="/personal/features" element={<PersonalFeaturesPage />} />
            <Route path="/personal/pricing" element={<PersonalPricingPage />} />
            {/* Guess-proofing: inverted URL shapes (/pricing/{audience}) that
                external/marketing/SEO links or users may guess wrong → 301 to
                the canonical /{audience}/pricing so they never hit a 404. */}
            <Route path="/pricing/personal" element={<Navigate to="/personal/pricing" replace />} />
            <Route path="/pricing/business" element={<Navigate to="/business/pricing" replace />} />
            <Route path="/pricing/government" element={<Navigate to="/government/pricing" replace />} />
            <Route path="/pricing/free-weekly" element={<Navigate to="/free-weekly" replace />} />
            <Route path="/pricing/free" element={<Navigate to="/free-weekly" replace />} />
            <Route path="/en/pricing/personal" element={<Navigate to="/en/personal/pricing" replace />} />
            <Route path="/en/pricing/business" element={<Navigate to="/en/business/pricing" replace />} />
            <Route path="/en/pricing/government" element={<Navigate to="/en/government/pricing" replace />} />
            <Route path="/en/pricing/free-weekly" element={<Navigate to="/en/free-weekly" replace />} />
            <Route path="/ja/pricing/personal" element={<Navigate to="/ja/personal/pricing" replace />} />
            <Route path="/ja/pricing/business" element={<Navigate to="/ja/business/pricing" replace />} />
            <Route path="/ja/pricing/government" element={<Navigate to="/ja/government/pricing" replace />} />
            <Route path="/ja/pricing/free-weekly" element={<Navigate to="/ja/free-weekly" replace />} />
            <Route path="/pricing-calculator-preview" element={<PricingCalculatorPreviewPage />} />
            {/* Iter 92 — Retired Limited-Time Offer pages all redirect to the single /free-trial conversion page */}
            <Route path="/personal/limited-time-offer" element={<Navigate to="/free-trial" replace />} />
            <Route path="/ja/personal/limited-time-offer" element={<Navigate to="/ja/free-trial" replace />} />
            <Route path="/en/personal/limited-time-offer" element={<Navigate to="/en/free-trial" replace />} />
            <Route path="/business/limited-time-offer" element={<Navigate to="/free-trial" replace />} />
            <Route path="/ja/business/limited-time-offer" element={<Navigate to="/ja/free-trial" replace />} />
            <Route path="/en/business/limited-time-offer" element={<Navigate to="/en/free-trial" replace />} />
            <Route path="/business" element={<BusinessOverviewPage />} />
            <Route path="/business/features" element={<BusinessFeaturesPage />} />
            <Route path="/business/pricing" element={<BusinessPricingPage />} />
            <Route path="/business/authorized-vehicles" element={<BusinessAuthorizedVehiclesDetailPage />} />
            {/* Organization Management explanation (Business) */}
            <Route path="/business/organization-management" element={<OrganizationManagementPage track="business" />} />
            <Route path="/ja/business/organization-management" element={<OrganizationManagementPage track="business" />} />
            <Route path="/en/business/organization-management" element={<OrganizationManagementPage track="business" />} />
            <Route path="/features" element={<FeaturesHubPage />} />
            <Route path="/features/:slug" element={<FeatureDetailPage />} />
            <Route path="/en/features" element={<FeaturesHubPage />} />
            <Route path="/en/features/:slug" element={<FeatureDetailPage />} />
            <Route path="/ja/features" element={<FeaturesHubPage />} />
            <Route path="/ja/features/:slug" element={<FeatureDetailPage />} />
            {/* ── Government & Public-Sector track ── */}
            <Route path="/government" element={<GovernmentOverviewPage />} />
            {/* Organization Management explanation (Government) */}
            <Route path="/government/organization-management" element={<OrganizationManagementPage track="government" />} />
            <Route path="/ja/government/organization-management" element={<OrganizationManagementPage track="government" />} />
            <Route path="/en/government/organization-management" element={<OrganizationManagementPage track="government" />} />
            <Route path="/government/features" element={<GovernmentFeaturesPage />} />
            <Route path="/government/pricing" element={<GovernmentPricingPage />} />
            <Route path="/government/authorized-vehicles" element={<AuthorizedVehiclesDetailPage />} />
            <Route path="/government/limited-time-offer" element={<Navigate to="/free-trial" replace />} />
            <Route path="/ja/government" element={<GovernmentOverviewPage />} />
            <Route path="/en/government" element={<GovernmentOverviewPage />} />
            <Route path="/ja/government/features" element={<GovernmentFeaturesPage />} />
            <Route path="/en/government/features" element={<GovernmentFeaturesPage />} />
            <Route path="/ja/government/pricing" element={<GovernmentPricingPage />} />
            <Route path="/en/government/pricing" element={<GovernmentPricingPage />} />
            <Route path="/ja/government/authorized-vehicles" element={<AuthorizedVehiclesDetailPage />} />
            <Route path="/en/government/authorized-vehicles" element={<AuthorizedVehiclesDetailPage />} />
            <Route path="/ja/government/limited-time-offer" element={<Navigate to="/ja/free-trial" replace />} />
            <Route path="/en/government/limited-time-offer" element={<Navigate to="/en/free-trial" replace />} />
            <Route path="/government/plate-types" element={<PlateTypesPage />} />
            <Route path="/ja/government/plate-types" element={<PlateTypesPage />} />
            <Route path="/en/government/plate-types" element={<PlateTypesPage />} />
            {/* Bilingual mirrors */}
            <Route path="/ja/personal" element={<PersonalOverviewPage />} />
            <Route path="/en/personal" element={<PersonalOverviewPage />} />
            <Route path="/ja/personal/features" element={<PersonalFeaturesPage />} />
            <Route path="/en/personal/features" element={<PersonalFeaturesPage />} />
            <Route path="/ja/personal/pricing" element={<PersonalPricingPage />} />
            <Route path="/en/personal/pricing" element={<PersonalPricingPage />} />
            <Route path="/ja/pricing-calculator-preview" element={<PricingCalculatorPreviewPage />} />
            <Route path="/en/pricing-calculator-preview" element={<PricingCalculatorPreviewPage />} />
            <Route path="/ja/business" element={<BusinessOverviewPage />} />
            <Route path="/en/business" element={<BusinessOverviewPage />} />
            <Route path="/ja/business/features" element={<BusinessFeaturesPage />} />
            <Route path="/en/business/features" element={<BusinessFeaturesPage />} />
            <Route path="/ja/business/pricing" element={<BusinessPricingPage />} />
            <Route path="/en/business/pricing" element={<BusinessPricingPage />} />
            <Route path="/ja/business/authorized-vehicles" element={<BusinessAuthorizedVehiclesDetailPage />} />
            <Route path="/en/business/authorized-vehicles" element={<BusinessAuthorizedVehiclesDetailPage />} />

            {/* Legacy redirect — /founder-program was consolidated into
                /free-trial. Preserves inbound links + SEO without shipping
                a duplicate page. */}
            <Route path="/founder-program" element={<Navigate to="/free-trial" replace />} />
            <Route path="/en/founder-program" element={<Navigate to="/en/free-trial" replace />} />
            <Route path="/ja/founder-program" element={<Navigate to="/ja/free-trial" replace />} />

            {/* Iter 277 (2026-05-24): legacy / external `/guides/` variants of the canonical
                receipt-guide URL.  Internal links and the sitemap already use the canonical
                `/etc-ryoshusho-riyoumeisai-guide` and `/japan-etc-receipt-guide` paths
                (no `/guides/` prefix).  These redirects exist so that any external link or
                cached SERP result for `/guides/etc-ryoshusho-riyoumeisai-guide` returns the
                canonical receipt guide instead of a 404. */}
            <Route path="/guides/etc-ryoshusho-riyoumeisai-guide" element={<Navigate to="/etc-ryoshusho-riyoumeisai-guide" replace />} />
            <Route path="/ja/guides/etc-ryoshusho-riyoumeisai-guide" element={<Navigate to="/ja/etc-ryoshusho-riyoumeisai-guide" replace />} />
            <Route path="/en/guides/etc-ryoshusho-riyoumeisai-guide" element={<Navigate to="/en/etc-ryoshusho-riyoumeisai-guide" replace />} />
            <Route path="/guides/japan-etc-receipt-guide" element={<Navigate to="/japan-etc-receipt-guide" replace />} />
            <Route path="/ja/guides/japan-etc-receipt-guide" element={<Navigate to="/ja/japan-etc-receipt-guide" replace />} />
            <Route path="/en/guides/japan-etc-receipt-guide" element={<Navigate to="/en/japan-etc-receipt-guide" replace />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          <Toaster />
          <ConditionalStickySupport />
          <EarlyAccessSideButton />
          <ConditionalPublicStatus />
          </SmartTooltipProvider>
        </AuthProvider>
        </AudienceProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;

